import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "../api/baseQuery";

export type CallDashboardStats = {
  total_calls: number;
  new_patients: number;
  existing_patients: number;
};

export type CallSkill = {
  id: number;
  code: string;
  label: string;
};

export type CallDashboardCall = {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  date: string; // ISO timestamp
  // New fields
  duration: number | null;
  summary: string | null;
  from_number: string | null;
  skills: CallSkill[];
};

export type PaginationInfo = {
  count: number;
  next: string | null;
  previous: string | null;
  page_size: number;
};

export type CallDashboardData = {
  calls: CallDashboardCall[];
  stats: CallDashboardStats;
  pagination: PaginationInfo;
};

export type CallSkillsResponse = {
  skills: CallSkill[];
};

export type CallTranscriptResponse = {
  id: number;
  transcript: string;
  call_identifier: string | null;
};

export const callsApi = createApi({
  reducerPath: "callsApi",
  tagTypes: ["CallDashboard"],
  baseQuery: baseQueryWithAuth,
  endpoints: (build) => ({
    getCallDashboardData: build.query<CallDashboardData, { page?: number }>({
      query: ({ page = 1 } = {}) => ({
        url: `/ai_admin/calls/dashboard/`,
        params: { page },
      }),
      providesTags: [{ type: "CallDashboard", id: "DATA" }],
    }),
    getArchivedCallDashboardData: build.query<CallDashboardData, { page?: number }>({
      query: ({ page = 1 } = {}) => ({
        url: `/ai_admin/calls/dashboard/`,
        params: { page, archived: true },
      }),
      providesTags: [{ type: "CallDashboard", id: "ARCHIVED" }],
    }),
    archiveCall: build.mutation<{ status: string }, number>({
      query: (callId) => ({
        url: `/ai_admin/calls/${callId}/archive/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "CallDashboard", id: "DATA" },
        { type: "CallDashboard", id: "ARCHIVED" },
      ],
    }),
    unarchiveCall: build.mutation<{ status: string }, number>({
      query: (callId) => ({
        url: `/ai_admin/calls/${callId}/unarchive/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "CallDashboard", id: "DATA" },
        { type: "CallDashboard", id: "ARCHIVED" },
      ],
    }),
    getCallSkills: build.query<CallSkillsResponse, void>({
      query: () => `/ai_admin/skills/`,
    }),
    getCallTranscript: build.query<CallTranscriptResponse, number>({
      query: (callId) => `/ai_admin/calls/${callId}/transcript/`,
    }),
  }),
});

export const {
  useGetCallDashboardDataQuery,
  useGetArchivedCallDashboardDataQuery,
  useArchiveCallMutation,
  useUnarchiveCallMutation,
  useGetCallSkillsQuery,
  useGetCallTranscriptQuery,
} = callsApi;
